import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import { Show } from '@side/pantry';
import { useEffect, useRef } from 'react';

export interface InfiniteScrollLoaderProps {
  onLoadMore: () => void;
  loading: boolean;
  loadingContent?: React.ReactNode;
  /** Scrollable ancestor to observe against; pass it whenever the list scrolls inside an inner container rather than the viewport */
  scrollRootRef?: React.RefObject<HTMLElement | null>;
}

export const InfiniteScrollLoader = ({
  onLoadMore,
  loading,
  loadingContent,
  scrollRootRef,
}: InfiniteScrollLoaderProps) => {
  const endOfListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const endOfList = endOfListRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        // Entries are batched oldest-first; a stale not-intersecting record can
        // precede the transition that matters, so only the latest reflects reality
        if (entries[entries.length - 1].isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: scrollRootRef?.current ?? null,
        rootMargin: '200px 0px',
        threshold: 0,
      },
    );

    if (endOfList) {
      observer.observe(endOfList);
    }

    return () => {
      if (endOfList) {
        observer.unobserve(endOfList);
      }
    };
  }, [endOfListRef, onLoadMore, scrollRootRef]);

  return (
    <>
      <Box ref={endOfListRef} />

      <Show when={loading}>
        <Show
          when={loadingContent}
          fallback={
            <Box
              sx={{
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <CircularProgress
                aria-label="loading more"
                size={16}
                sx={{ color: 'inherit' }}
              />
            </Box>
          }
        >
          {loadingContent}
        </Show>
      </Show>
    </>
  );
};
